import { useEffect, useState, useMemo } from "react";
import { Outlet, useNavigate, NavLink } from "react-router-dom";
import { LogOut, Moon, ReceiptText, RefreshCw, ScrollText, Sun, X } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { featureAllowed } from "@/lib/access";
import { peerManager } from "@/lib/captainMesh/transport/peerManager";
import { restoreActorSession } from "@/lib/captainMesh/transport/identity";
import LicenseGate from "@/components/LicenseGate";
import { failoverMonitor } from "@/lib/captainMesh/application/failoverMonitor";
import { syncService } from "@/lib/captainMesh/application/syncService";
import { pruneDeadAggregates } from "@/lib/captainMesh/db";
import { getMeshBase } from "@/lib/captainMesh/routes";
import { forceSyncDevice } from "@/lib/forceSync";
import ConnectMyTelegramButton from "@/components/ConnectMyTelegram";
import { CaptainAttendanceWidget } from "@/components/captain/CaptainAttendanceWidget";
import { AttendancePrompts } from "@/components/captain/AttendancePrompts";
import { AttendanceProvider } from "@/hooks/useAttendance";
import { useSignOut } from "@/components/SignOutDialog";
import { linkAllowed, routeGroups } from "@/components/AppShell";
import UnifiedMobileDrawer from "@/components/UnifiedMobileDrawer";

export const TOPBAR_HEIGHT = 56;
export const MOBILE_NAV_HEIGHT = 0;

export default function CaptainLayout() {
  const { theme, setTheme, logout, token, user, permissions, checkServer } = usePOS();
  const [reloading, setReloading] = useState(false);
  const pendingAllowed = featureAllowed("orders.pending_bills", user, permissions);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);

  // A reload wipes the in-memory actor session, and every punch then threw "No
  // active actor session" inside submitEvent — the ADD button did nothing at
  // all: no row, no toast, no error, mid-service. restoreActorSession re-arms it
  // from the key IndexedDB kept, so a refresh is now a non-event. The PIN screen
  // is only for a device that genuinely has no key yet (first use, or after a
  // sign-out cleared it).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      // A reload drops the in-memory key; IndexedDB still holds a usable
      // non-extractable handle on it, so refreshing is now a non-event.
      if (await restoreActorSession()) return;
      if (cancelled) return;
      fallBackToPin();
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  const fallBackToPin = () => {
    toast.error("Enter your PIN again to punch orders — this device has no signing key yet.", {
      duration: 6000,
      id: "actor-session-lost",
    });
    // Send them to the PIN screen but do NOT logout(): the POS session itself is
    // still perfectly good, and tearing it down would sign an admin out of the
    // whole app just for opening a captain URL. Re-entering the PIN rebuilds the
    // actor key and login() overwrites the session in place.
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    console.log("[CaptainLayout] Initializing mesh and sync modules", !!syncService);
    syncService.init();
    pruneDeadAggregates().catch(() => {});

    try {
      const probe = document.createElement("div");
      probe.style.cssText = "position:fixed;top:0;visibility:hidden;padding-top:env(safe-area-inset-top,0px);";
      document.body.appendChild(probe);
      const envPad = parseFloat(getComputedStyle(probe).paddingTop) || 0;
      probe.remove();
      if (envPad === 0 && typeof window.fully !== "undefined") {
        const stored = parseInt(localStorage.getItem("vibez-kiosk-top-inset") || "", 10);
        const pad = Number.isFinite(stored) ? stored : 36;
        document.documentElement.style.setProperty("--kiosk-top-pad", `${pad}px`);
      }
    } catch { }

    syncService.onConflict = (conflicts) => {
      for (const c of conflicts) {
        // A busy table joins the running order on the server now, so what
        // reaches here is a real refusal. Say which, once per aggregate.
        const detail = c.detail || {};
        const why =
          c.code === "ADOPTION_STALE"
            ? "that order was closed on another terminal"
            : typeof detail === "string"
              ? detail
              : detail.message || detail.detail || c.code || "the server refused it";
        toast.error(`Items not sent: ${why}. Open the table again and re-punch them.`, {
          duration: 8000,
          id: `table-conflict-${c.aggregate_id}`,
        });
      }
    };
    
    peerManager.start();
    // The page's own server. Production builds with REACT_APP_BACKEND_URL="", and
    // the old 127.0.0.1:8000 fallback sent every pad's signaling to itself: no two
    // pads linked once in 21-24 Oct (122 attempts, 0 opened).
    peerManager.initialize(process.env.REACT_APP_BACKEND_URL || window.location.origin, null, "DEFAULT_RESTAURANT", token);
    failoverMonitor.start();
    return () => {
      syncService.onConflict = null;
      failoverMonitor.stop();
    };
  }, [token]);

  // Reload sends everything this pad owes the server first (owner, 2026-10-31).
  // Anything still unsent stays on screen with the server's reason, and the
  // reload waits for a tap, so the reason is not wiped away by the reload.
  const reloadWithSync = async () => {
    if (reloading) return;
    setReloading(true);
    let result;
    try {
      result = await forceSyncDevice(token, checkServer);
    } catch (error) {
      result = { ok: false, message: `Sync failed on this device: ${error?.message || error}` };
    }
    if (result.ok) {
      window.location.reload();
      return;
    }
    setReloading(false);
    toast.error(result.message, {
      duration: 15000,
      action: { label: "Reload anyway", onClick: () => window.location.reload() },
    });
  };

  const { signOut: exit, dialog: signOutDialog } = useSignOut(token, () => { logout(); navigate("/login"); });

  const visibleGroups = useMemo(() => {
    return routeGroups.map(group => ({
      ...group,
      // The main site's /logging opens under AppShell; inside the captain shell the
      // captain section's own Logging entry (routed under /captain) is the one to use,
      // so an admin here is not shown two different "Logging" links.
      // Screens follow the person's own switches, as in the main shell: a captain
      // given Reports saw no way to it here (Saket, 2026-10-30).
      links: group.links.filter(link => linkAllowed(link, user, permissions) && link.to !== "/logging" && link.to !== "/captain")
    })).filter(group => group.links.length > 0);
  }, [user, permissions]);

  return (
    <AttendanceProvider token={token}>
    <div
      className="h-app w-full app-noselect bg-background flex flex-col lg:flex-row overflow-hidden"
      style={{ "--vz-topbar-h": `${TOPBAR_HEIGHT}px`, "--vz-mobile-nav-h": `${MOBILE_NAV_HEIGHT}px` }}
      data-testid="captain-shell"
    >
      {/* Desktop Sidebar */}
      {desktopSidebarOpen && (
        <aside className="hidden lg:flex flex-col w-64 border-r bg-card h-screen shrink-0 z-50" data-testid="desktop-sidebar">
          <div className="h-[var(--vz-topbar-h)] flex items-center px-loose border-b shrink-0">
            <span className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold text-lede mr-snug shadow-inner">V</span>
            <strong className="text-title tracking-tight text-card-foreground">Vibez</strong>
          </div>

          <nav className="flex-1 overflow-y-auto py-base px-base space-y-loose" data-testid="primary-navigation">
            {visibleGroups.map(group => (
              <div key={group.name} className="space-y-tight">
                <div className="px-snug text-micro font-bold uppercase text-muted-foreground mb-tight">{group.name}</div>
                {group.links.map(({ to, end, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-")}`}
                    title={label}
                    className={({isActive}) => `flex items-center gap-base px-snug py-snug rounded-md text-body font-medium transition-colors ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                  >
                    <Icon className="w-5 h-5"/>
                    {label}
                  </NavLink>
                ))}
              </div>
            ))}
            <div className="space-y-tight">
              <div className="px-snug text-micro font-bold uppercase text-muted-foreground mb-tight">Captain</div>
              {/* Pending bills was only in the phone drawer; a captain on a tablet or
                  PC had no way to it (HANDOVER open item 6). */}
              {pendingAllowed && (
                <NavLink
                  to={`${getMeshBase()}/pending-bills`}
                  data-testid="captain-sidebar-pending-bills"
                  title="Pending bills"
                  className={({isActive}) => `flex items-center gap-base px-snug py-snug rounded-md text-body font-medium transition-colors ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                >
                  <ReceiptText className="w-5 h-5"/>
                  Pending bills
                </NavLink>
              )}
              <NavLink
                to="/captain/logging"
                data-testid="captain-sidebar-logging"
                title="Logging"
                className={({isActive}) => `flex items-center gap-base px-snug py-snug rounded-md text-body font-medium transition-colors ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              >
                <ScrollText className="w-5 h-5"/>
                Logging
              </NavLink>
            </div>
          </nav>

          <div className="p-base border-t flex items-center justify-between gap-2">
            <CaptainAttendanceWidget token={token} />
            <div className="flex items-center gap-1">
              <button
                onClick={() => setTheme(theme === "light" ? "dark" : "light")}
                className="p-snug rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                data-testid="theme-toggle-button"
                aria-label="Toggle colour theme"
              >
                {theme === "light" ? <Moon className="w-5 h-5"/> : <Sun className="w-5 h-5"/>}
              </button>
              <button
                onClick={exit}
                className="p-snug rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                data-testid="logout-button"
                aria-label="Sign out"
              >
                <LogOut className="w-5 h-5"/>
              </button>
            </div>
          </div>
        </aside>
      )}

      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        <main className="flex-1 flex flex-col relative min-h-0 overflow-hidden bg-background">
          {/* The gate belongs HERE, not on the route element. Protected renders
              <Shell><ErrorBoundary>{children}</ErrorBoundary></Shell>, but this
              layout renders its own <Outlet> and drops `children` — so the
              LicenseGate that App.js wrapped around the captain route never
              mounted and the CaptainPad add-on was never actually checked. */}
          <LicenseGate addon="captainpad">
            <Outlet context={{ openDrawer: () => setDrawerOpen(true), toggleDesktopSidebar: () => setDesktopSidebarOpen(prev => !prev) }} />
          </LicenseGate>
        </main>
      </div>

      <UnifiedMobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        groups={visibleGroups}
        user={user}
        permissions={permissions}
        theme={theme}
        setTheme={setTheme}
        onExit={exit}
        token={token}
        checkServer={checkServer}
        isCaptainLayout={true}
      />
      {signOutDialog}
      {/* The split-break prompt and START DUTY bar, once per screen. */}
      <AttendancePrompts token={token} />
    </div>
    </AttendanceProvider>
  );
}
